import { createSlice } from '@reduxjs/toolkit'
import { socketDestroy } from 'hooks/useSocket'
import { MessageInfoModel, MessageUnreadInfoModel } from 'services/types/Message'
import {
  AccountAssets,
  NoticeModel,
  UserAssetsBalanceResModel,
  UserCabitalInfoResModel,
  UserModel,
} from 'services/types/User'
import {
  setProfileInfo,
  logoutThunk,
  initPublicKey,
  setUserCabitalInfo,
  setUserAssets,
  setMessageInfo,
  setMessageUnreadInfo,
} from './actions'
import FlutterAppData from 'services/flutterAppConnection'
import Cookies from 'js-cookie'
import sensors from 'sa-sdk-javascript'

export interface UserState {
  readonly userInfo: UserModel | null
  readonly assets: AccountAssets
  readonly publicKey: string
  readonly processing: number
  readonly noticeData: NoticeModel | null
  readonly cabitalInfo: UserCabitalInfoResModel
  readonly userCabitalBalance: Array<UserAssetsBalanceResModel>
  readonly cabitalCurrency: string
  readonly refreshNftType: string
  readonly messageInfo: MessageInfoModel
  readonly messageUnreadInfo: MessageUnreadInfoModel
}

const initialState: UserState = {
  userInfo: null,
  assets: {
    coinMsgList: [],
    tokenMsgList: [],
  },
  messageUnreadInfo: {
    sysNum: 0,
    commentNum: 0,
    dynamicNum: 0,
  },
  messageInfo: {
    unreadNum: 0,
    messageConfigRsp: {
      bind: 0,
      buy: 0,
      comments: 0,
      craft: 0,
      desktopNotifications: 0,
      follows: 0,
      gifts: 0,
      likes: 0,
      mint: 0,
      open: 0,
      priceAlert: 0,
      sell: 0,
    },
  },
  processing: 0,
  publicKey: '',
  cabitalCurrency: 'EUR',
  noticeData: null,
  userCabitalBalance: [],
  refreshNftType: '',
  cabitalInfo: {
    fetched: false,
    linked: false,
    otpReady: false,
    kyc: false,
    email: '',
    linkUrl: '',
  },
}

const UserSlice = createSlice({
  name: 'user',
  initialState,
  extraReducers: builder => {
    builder.addCase(setUserCabitalInfo.fulfilled, (state, { payload }) => {
      state.cabitalInfo = payload.cabitalInfo
      state.userCabitalBalance = payload.cabitalBalances
    })
    builder.addCase(setUserAssets.fulfilled, (state, { payload }) => {
      if (payload.code === 200) {
        state.assets = payload.data
      }
    })
    builder.addCase(setProfileInfo.fulfilled, (state, { payload }) => {
      if (payload.code === 200) {
        state.userInfo = payload.data
        window.sessionStorage.setItem('user', JSON.stringify(payload.data))
      } else {
        window.sessionStorage.removeItem('user')
      }
    })
    builder.addCase(initPublicKey.fulfilled, (state, { payload }) => {
      if (payload.code === 200) {
        state.publicKey = payload.data
      }
    })
    builder.addCase(setMessageInfo.fulfilled, (state, { payload }) => {
      if (payload.code === 200) {
        state.messageInfo = payload.data
      }
    })
    builder.addCase(setMessageUnreadInfo.fulfilled, (state, { payload }) => {
      if (payload.code === 200) {
        state.messageUnreadInfo = payload.data
      }
    })
    builder.addCase(logoutThunk.fulfilled, (state, { payload }) => {
      if (payload.code === 200) {
        FlutterAppData.removeAppData()
        localStorage.removeItem('token')
        Cookies.remove('token')
        sessionStorage.removeItem('user')
        sensors.logout()
        state.userInfo = null
        socketDestroy()
        if (
          !window.location.pathname.includes('game/OathofPeak/earlyaccess') &&
          !window.location.pathname.includes('referral')
        ) {
          window.location.href = '/'
        }
      }
    })
  },
  reducers: {
    updateCabitalCurrency(state, action) {
      state.cabitalCurrency = action.payload
    },
    updateNoticeData(state, action) {
      state.noticeData = action.payload
    },
    updateProcessing(state, action) {
      state.processing = action.payload
    },
    updateUserName(state, action) {
      if (state.userInfo && action.payload) {
        state.userInfo.nickName = action.payload
      }
    },
    updateAvatar(state, action) {
      if (state.userInfo && action.payload) {
        state.userInfo.headIcon = action.payload
      }
    },
    updateUserAssets(state, action) {
      state.assets = action.payload
    },
    updateRefreshNftType(state, action) {
      state.refreshNftType = action.payload
    },
    updateBindingAccounts(state, action) {
      if (state.userInfo && action.payload) {
        const index = state.userInfo.bindingAccounts.findIndex(
          el => el.type.toLocaleLowerCase() === action.payload.toLocaleLowerCase()
        )
        if (index > -1) {
          state.userInfo.bindingAccounts[index].state = 1 - state.userInfo.bindingAccounts[index].state
        }
      }
    },
    updateReadMessage(state, action) {
      state.messageInfo.unreadNum = state.messageInfo.unreadNum - action.payload
    },
    updateMessageConfig(state, action) {
      state.messageInfo.messageConfigRsp = { ...state.messageInfo.messageConfigRsp, ...action.payload }
    },
    updateMessageUnreadInfo(state, action) {
      state.messageUnreadInfo = action.payload
      state.messageInfo.unreadNum = action.payload.sysNum + action.payload.commentNum + action.payload.dynamicNum
    },
  },
})

export const {
  updateUserName,
  updateAvatar,
  updateBindingAccounts,
  updateUserAssets,
  updateProcessing,
  updateCabitalCurrency,
  updateNoticeData,
  updateRefreshNftType,
  updateMessageConfig,
  updateMessageUnreadInfo,
} = UserSlice.actions
export default UserSlice.reducer
